import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faCheck,
  faSpinner,
  faTimes,
  faChevronLeft,
  faChevronRight,
  faPhone,
  faEdit,
  faUserPlus
} from '@fortawesome/free-solid-svg-icons';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';

const DROPDOWN_PAGE_SIZE = 4;
const MODAL_PAGE_SIZE = 5;

const AssignClientSelector = ({ ticket, onTicketUpdated, triggerRef, hideButton = false }) => {
  const { token, nombreCompleto } = useToken();
  const { showErrorToast, showSuccessToast } = useToast();

  // Dropdown State (Forma 1: Rápida con scroll infinito de 4 en 4)
  const [showAssignDropdown, setShowAssignDropdown] = useState(false);
  const [matchingClients, setMatchingClients] = useState([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreClients, setHasMoreClients] = useState(true);
  const [dropdownPage, setDropdownPage] = useState(1);
  const [dropdownSearch, setDropdownSearch] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);

  // Modal State (Forma 2: Búsqueda con paginación separada)
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [modalSearchTerm, setModalSearchTerm] = useState('');
  const [modalClients, setModalClients] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalPage, setModalPage] = useState(1);
  const [modalTotalPages, setModalTotalPages] = useState(1);
  const [modalTotalRecords, setModalTotalRecords] = useState(0);

  const dropdownRef = useRef(null);
  const scrollContainerRef = useRef(null);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowAssignDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cargar clientes para el dropdown (Forma 1)
  const loadDropdownClients = async (query, page = 1, append = false) => {
    if (page === 1) {
      setIsLoadingClients(true);
    } else {
      setIsLoadingMore(true);
    }

    try {
      const url = `/api/SupportEngine/GetClientsByPhone?phone=${encodeURIComponent(query || '')}&pageNumber=${page}&pageSize=${DROPDOWN_PAGE_SIZE}`;
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + token
        }
      });
      if (response.ok) {
        const result = await response.json();
        const clients = result.data || result;
        const list = Array.isArray(clients) ? clients : [];

        if (append) {
          setMatchingClients(prev => [...prev, ...list]);
        } else {
          setMatchingClients(list);
        }

        setHasMoreClients(list.length >= DROPDOWN_PAGE_SIZE);
      }
    } catch (err) {
      console.error('Error al cargar clientes coincidentes:', err);
    } finally {
      setIsLoadingClients(false);
      setIsLoadingMore(false);
    }
  };

  // Cargar clientes y paginación en el Modal (Forma 2 - SPs independientes)
  const loadModalData = async (query, page = 1) => {
    setModalLoading(true);
    try {
      const trimmedQuery = query.trim();

      // 1. Consultar registros de la página (SP SearchClientsPaged)
      const listPromise = fetch(
        `/api/SupportEngine/SearchClientsPaged?searchTerm=${encodeURIComponent(trimmedQuery)}&pageNumber=${page}&pageSize=${MODAL_PAGE_SIZE}`,
        { headers: { 'Accept': 'application/json', 'Authorization': 'Bearer ' + token } }
      );

      // 2. Consultar conteo y total de páginas (SP GetClientsPagination)
      const paginationPromise = fetch(
        `/api/SupportEngine/GetClientsPagination?searchTerm=${encodeURIComponent(trimmedQuery)}&pageSize=${MODAL_PAGE_SIZE}`,
        { headers: { 'Accept': 'application/json', 'Authorization': 'Bearer ' + token } }
      );

      const [listRes, pagRes] = await Promise.all([listPromise, paginationPromise]);

      if (listRes.ok) {
        const listJson = await listRes.json();
        const clients = listJson.data || listJson;
        setModalClients(Array.isArray(clients) ? clients : []);
      }

      if (pagRes.ok) {
        const pagJson = await pagRes.json();
        const pagData = pagJson.data || pagJson;
        const total = pagData.totalRecords || 0;
        setModalTotalRecords(total);
        setModalTotalPages(total > 0 ? Math.ceil(total / MODAL_PAGE_SIZE) : 1);
      }
    } catch (err) {
      console.error('Error al cargar datos de modal:', err);
    } finally {
      setModalLoading(false);
    }
  };

  const lastFetchedDropdownSearchRef = useRef(null);
  const lastFetchedModalSearchRef = useRef(null);

  // Abrir / Cerrar Dropdown
  const handleToggleDropdown = () => {
    const nextState = !showAssignDropdown;
    setShowAssignDropdown(nextState);
    if (nextState) {
      const phoneToSearch = ticket.ticketPhone || ticket.phone || '';
      setDropdownSearch(phoneToSearch);
      setDropdownPage(1);
      lastFetchedDropdownSearchRef.current = phoneToSearch;
      loadDropdownClients(phoneToSearch, 1, false);
    } else {
      lastFetchedDropdownSearchRef.current = null;
    }
  };

  useEffect(() => {
    if (triggerRef) {
      triggerRef.current = handleToggleDropdown;
    }
  }, [triggerRef]);

  // Abrir Modal de Búsqueda
  const handleOpenModal = () => {
    setShowAssignDropdown(false);
    lastFetchedDropdownSearchRef.current = null;
    setShowSearchModal(true);
    const initialQuery = dropdownSearch || ticket.ticketPhone || ticket.phone || '';
    setModalSearchTerm(initialQuery);
    setModalPage(1);
    lastFetchedModalSearchRef.current = initialQuery;
    loadModalData(initialQuery, 1);
  };

  // Cerrar Modal de Búsqueda
  const handleCloseModal = () => {
    setShowSearchModal(false);
    lastFetchedModalSearchRef.current = null;
  };

  // Debounce búsqueda en dropdown (solo cuando el usuario teclea un término diferente)
  useEffect(() => {
    if (!showAssignDropdown) return;
    if (lastFetchedDropdownSearchRef.current === dropdownSearch) return;

    const timer = setTimeout(() => {
      lastFetchedDropdownSearchRef.current = dropdownSearch;
      setDropdownPage(1);
      loadDropdownClients(dropdownSearch.trim(), 1, false);
    }, 300);
    return () => clearTimeout(timer);
  }, [dropdownSearch, showAssignDropdown]);

  // Debounce búsqueda en modal (solo cuando el usuario teclea un término diferente)
  useEffect(() => {
    if (!showSearchModal) return;
    if (lastFetchedModalSearchRef.current === modalSearchTerm) return;

    const timer = setTimeout(() => {
      lastFetchedModalSearchRef.current = modalSearchTerm;
      setModalPage(1);
      loadModalData(modalSearchTerm.trim(), 1);
    }, 300);
    return () => clearTimeout(timer);
  }, [modalSearchTerm, showSearchModal]);

  // Scroll infinito en dropdown
  const handleDropdownScroll = () => {
    if (!scrollContainerRef.current || isLoadingMore || !hasMoreClients || isLoadingClients) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 30) {
      const nextPage = dropdownPage + 1;
      setDropdownPage(nextPage);
      loadDropdownClients(dropdownSearch.trim(), nextPage, true);
    }
  };

  // Paginación en modal (Página anterior / siguiente)
  const handlePrevPage = () => {
    if (modalPage > 1 && !modalLoading) {
      const prev = modalPage - 1;
      setModalPage(prev);
      loadModalData(modalSearchTerm.trim(), prev);
    }
  };

  const handleNextPage = () => {
    if (modalPage < modalTotalPages && !modalLoading) {
      const next = modalPage + 1;
      setModalPage(next);
      loadModalData(modalSearchTerm.trim(), next);
    }
  };

  // Asignar cliente seleccionado al ticket
  const handleSelectClient = async client => {
    if (!client || !ticket || isAssigning) return;
    const ticketId = ticket.idTicket || ticket.id;
    if (!ticketId) return;

    setIsAssigning(true);

    try {
      const response = await fetch('/api/SupportEngine/AssignTicketClient', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify({
          idTicket: parseInt(ticketId),
          idClienteAkasia: client.idClienteAkasia
        })
      });

      if (response.ok) {
        const result = await response.json();
        const updated = result.data || result;
        showSuccessToast(`Ticket asignado a ${client.vNombreCompleto || 'Cliente'}`);
        setShowAssignDropdown(false);
        setShowSearchModal(false);
        if (onTicketUpdated) {
          onTicketUpdated(updated);
        }
      } else {
        const errData = await response.json().catch(() => ({}));
        showErrorToast(errData.message || 'Error al asignar el cliente al ticket.');
      }
    } catch (err) {
      showErrorToast('Error de red al asignar cliente: ' + err.message);
    } finally {
      setIsAssigning(false);
    }
  };

  // Helper para generar los números de página visibles (ej. 1, 2, 3, 4, 5 ... 150)
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    const total = modalTotalPages || 1;

    if (total <= maxVisible + 2) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      pages.push(1);
      let start = Math.max(2, modalPage - 1);
      let end = Math.min(total - 1, modalPage + 1);

      if (modalPage <= 3) {
        start = 2;
        end = 4;
      } else if (modalPage >= total - 2) {
        start = total - 3;
        end = total - 1;
      }

      if (start > 2) pages.push('ellipsis-start');
      for (let i = start; i <= end; i++) pages.push(i);
      if (end < total - 1) pages.push('ellipsis-end');

      pages.push(total);
    }
    return pages;
  };

  const handleGoToPage = pageNum => {
    if (pageNum >= 1 && pageNum <= modalTotalPages && pageNum !== modalPage && !modalLoading) {
      setModalPage(pageNum);
      loadModalData(modalSearchTerm.trim(), pageNum);
    }
  };

  return (
    <div className="d-inline-flex" ref={dropdownRef}>
      {/* Ícono discreto a un lado del nombre (editar o agregar) */}
      {!hideButton && (
        <button
          type="button"
          className="btn btn-link text-muted p-1 text-decoration-none rounded-circle hover-bg-200"
          style={{ width: '26px', height: '26px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={handleToggleDropdown}
          title={ticket.idClienteAkasia ? 'Cambiar cliente asignado' : 'Asignar cliente a este ticket'}
        >
          <FontAwesomeIcon
            icon={ticket.idClienteAkasia ? faEdit : faUserPlus}
            className={ticket.idClienteAkasia ? 'text-secondary' : 'text-primary'}
            style={{ fontSize: '0.85rem' }}
          />
        </button>
      )}

      {/* Dropdown flotante perfectamente centrado en el sidebar */}
      {showAssignDropdown && (
        <div
          className="card shadow-lg border p-2 position-absolute text-start"
          style={{
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1050,
            width: '276px',
            maxWidth: 'calc(100% - 10px)',
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            marginTop: '8px',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden'
          }}
        >
          <div className="d-flex align-items-center justify-content-between pb-2 mb-2 border-bottom">
            <span className="fw-bold text-dark text-truncate me-2" style={{ fontSize: '0.78rem' }}>
              {ticket.phone ? `Coincidencias (${ticket.phone})` : 'Seleccionar Cliente'}
            </span>
            <button
              type="button"
              className="btn-close"
              style={{ fontSize: '0.55rem' }}
              onClick={() => setShowAssignDropdown(false)}
            ></button>
          </div>

          <div className="mb-2">
            <div className="input-group input-group-sm">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Buscar por teléfono o nombre..."
                style={{ fontSize: '0.75rem' }}
                value={dropdownSearch}
                onChange={e => setDropdownSearch(e.target.value)}
              />
              <button
                className="btn btn-outline-primary btn-sm"
                type="button"
                title="Búsqueda en lista completa"
                onClick={handleOpenModal}
              >
                <FontAwesomeIcon icon={faSearch} />
              </button>
            </div>
          </div>

          <div
            ref={scrollContainerRef}
            onScroll={handleDropdownScroll}
            className="overflow-y-auto pe-1"
            style={{ maxHeight: '220px', overflowY: 'auto' }}
          >
            {isLoadingClients ? (
              <div className="text-center py-3 text-muted" style={{ fontSize: '0.75rem' }}>
                <FontAwesomeIcon icon={faSpinner} spin className="me-1 text-success" />
                Buscando clientes...
              </div>
            ) : matchingClients.length === 0 ? (
              <div className="text-center py-3 text-muted" style={{ fontSize: '0.75rem' }}>
                <div>No se encontraron coincidencias.</div>
                <button
                  type="button"
                  className="btn btn-link btn-xs p-0 mt-1 text-primary"
                  style={{ fontSize: '0.72rem' }}
                  onClick={handleOpenModal}
                >
                  <FontAwesomeIcon icon={faSearch} className="me-1" />
                  Buscar en todos los clientes
                </button>
              </div>
            ) : (
              <>
                {matchingClients.map(c => {
                  const isSelected = ticket.idClienteAkasia === c.idClienteAkasia;
                  return (
                    <div
                      key={c.idClienteAkasia}
                      className={`p-2 rounded mb-1 cursor-pointer border ${isSelected ? 'border-success bg-success bg-opacity-10' : 'bg-light hover-bg-200'}`}
                      style={{ cursor: 'pointer', transition: 'background-color 0.15s' }}
                      onClick={() => handleSelectClient(c)}
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <div className="fw-bold text-dark" style={{ fontSize: '0.78rem' }}>
                            #{c.idClienteAkasia} - {c.vNombreCompleto || 'Sin Nombre'}
                          </div>
                          <div className="text-dark fw-medium" style={{ fontSize: '0.70rem' }}>
                            <FontAwesomeIcon icon={faPhone} className="me-1 text-secondary" />
                            {c.vTelefonoPersonal || c.vTelefonoTrabajo || 'Sin teléfono'}
                          </div>
                        </div>
                        {isSelected && (
                          <span className="badge bg-success" style={{ fontSize: '0.65rem' }}>
                            <FontAwesomeIcon icon={faCheck} />
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}

                {isLoadingMore && (
                  <div className="text-center py-2 text-muted" style={{ fontSize: '0.70rem' }}>
                    <FontAwesomeIcon icon={faSpinner} spin className="me-1 text-success" />
                    Cargando más clientes...
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal Sencillo y Limpio (Forma 2 con diseño refinado y SPs separados) */}
      {showSearchModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.25)',
            zIndex: 1060
          }}
          onClick={handleCloseModal}
        >
          <div
            className="modal-dialog modal-dialog-centered modal-lg"
            onClick={e => e.stopPropagation()}
          >
            <div
              className="modal-content border shadow-sm"
              style={{ borderRadius: '12px', overflow: 'hidden', backgroundColor: '#ffffff' }}
            >
              {/* Header */}
              <div className="modal-header bg-white border-bottom py-3 px-4">
                <div>
                  <h6 className="modal-title fw-bold text-dark mb-0 d-flex align-items-center" style={{ fontSize: '1.05rem' }}>
                    <FontAwesomeIcon icon={faSearch} className="text-primary me-2" style={{ fontSize: '0.95rem' }} />
                    <span>Búsqueda de Clientes</span>
                  </h6>
                  <small className="text-muted d-block mt-0" style={{ fontSize: '0.75rem' }}>
                    Ticket #{ticket.id} — Asignar cliente por nombre o teléfono
                  </small>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseModal}
                ></button>
              </div>

              {/* Body */}
              <div className="modal-body p-3 p-md-4">
                {/* Input Buscador */}
                <div className="mb-3">
                  <div className="input-group">
                    <span className="input-group-text bg-white border-end-0 text-muted ps-3">
                      <FontAwesomeIcon icon={faSearch} />
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0 ps-2"
                      style={{ fontSize: '0.90rem', height: '42px' }}
                      placeholder="Escribe número de teléfono o nombre del cliente..."
                      value={modalSearchTerm}
                      onChange={e => setModalSearchTerm(e.target.value)}
                      autoFocus
                    />
                    {modalSearchTerm && (
                      <button
                        className="btn btn-outline-secondary border-start-0 px-3"
                        type="button"
                        onClick={() => setModalSearchTerm('')}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Lista Paginada (Solo muestra #ID - Nombre) */}
                <div className="border rounded-3 p-2" style={{ minHeight: '260px', backgroundColor: '#ffffff' }}>
                  {modalLoading ? (
                    <div className="text-center py-5 text-muted">
                      <FontAwesomeIcon icon={faSpinner} spin className="me-2 text-primary fs-4" />
                      <div className="mt-2" style={{ fontSize: '0.85rem' }}>Buscando clientes...</div>
                    </div>
                  ) : modalClients.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <div className="fw-semibold mb-1" style={{ fontSize: '0.90rem' }}>No se encontraron clientes</div>
                      <small>Prueba buscando con otro término.</small>
                    </div>
                  ) : (
                    <div className="d-flex flex-column gap-2">
                      {modalClients.map(c => {
                        const isSelected = ticket.idClienteAkasia === c.idClienteAkasia;
                        return (
                          <div
                            key={c.idClienteAkasia}
                            className={`p-3 rounded-2 border d-flex justify-content-between align-items-center ${
                              isSelected
                                ? 'border-primary bg-primary bg-opacity-10'
                                : 'bg-white hover-bg-100'
                            }`}
                            style={{ cursor: 'pointer', transition: 'all 0.15s ease' }}
                            onClick={() => handleSelectClient(c)}
                          >
                            <div className="d-flex align-items-center gap-3">
                              <div
                                className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-dark"
                                style={{
                                  width: '36px',
                                  height: '36px',
                                  backgroundColor: isSelected ? '#dbeafe' : '#f1f5f9',
                                  fontSize: '0.80rem'
                                }}
                              >
                                {c.vNombreCompleto ? c.vNombreCompleto.substring(0, 2).toUpperCase() : 'CL'}
                              </div>
                              <div>
                                <div className="fw-bold text-dark" style={{ fontSize: '0.90rem' }}>
                                  #{c.idClienteAkasia} — {c.vNombreCompleto || 'Sin Nombre'}
                                </div>
                              </div>
                            </div>

                            <div>
                              {isSelected ? (
                                <span className="badge bg-primary px-3 py-2" style={{ fontSize: '0.75rem' }}>
                                  <FontAwesomeIcon icon={faCheck} className="me-1" /> Asignado
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary rounded-pill px-3 shadow-none"
                                  disabled={isAssigning}
                                  onClick={e => {
                                    e.stopPropagation();
                                    handleSelectClient(c);
                                  }}
                                >
                                  {isAssigning ? (
                                    <>
                                      <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                                      Asignando...
                                    </>
                                  ) : (
                                    'Seleccionar'
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Footer con Selector de Páginas Interactivo */}
              <div className="modal-footer bg-light py-2 px-4 border-top d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  {/* Botón Anterior */}
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary px-2 py-1 shadow-none"
                    style={{ fontSize: '0.80rem', borderRadius: '6px' }}
                    onClick={handlePrevPage}
                    disabled={modalPage <= 1 || modalLoading}
                  >
                    <FontAwesomeIcon icon={faChevronLeft} className="me-1" />
                    Anterior
                  </button>

                  {/* Números de página interactivos */}
                  <div className="d-flex align-items-center gap-1">
                    {getPageNumbers().map((p, idx) => {
                      if (typeof p === 'string') {
                        return (
                          <span key={p + idx} className="text-muted px-1" style={{ fontSize: '0.80rem' }}>
                            ...
                          </span>
                        );
                      }
                      const isCurrent = p === modalPage;
                      return (
                        <button
                          key={p}
                          type="button"
                          className={`btn btn-sm px-2 py-1 shadow-none fw-bold ${
                            isCurrent
                              ? 'btn-primary text-white'
                              : 'btn-outline-secondary border-0 bg-transparent text-dark hover-bg-200'
                          }`}
                          style={{
                            minWidth: '32px',
                            height: '30px',
                            fontSize: '0.80rem',
                            borderRadius: '6px'
                          }}
                          onClick={() => handleGoToPage(p)}
                          disabled={modalLoading}
                        >
                          {p}
                        </button>
                      );
                    })}
                  </div>

                  {/* Botón Siguiente */}
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary px-2 py-1 shadow-none"
                    style={{ fontSize: '0.80rem', borderRadius: '6px' }}
                    onClick={handleNextPage}
                    disabled={modalPage >= modalTotalPages || modalLoading}
                  >
                    Siguiente
                    <FontAwesomeIcon icon={faChevronRight} className="ms-1" />
                  </button>

                  {modalTotalRecords > 0 && (
                    <span className="text-muted ms-2" style={{ fontSize: '0.78rem' }}>
                      ({modalTotalRecords} clientes)
                    </span>
                  )}
                </div>

                <button
                  type="button"
                  className="btn btn-secondary btn-sm px-4 rounded-pill shadow-none"
                  style={{ fontSize: '0.82rem' }}
                  onClick={handleCloseModal}
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignClientSelector;
